import Typewriter from 'typewriter-effect/dist/core';
import initSplitTitle from './utils/init-split-title';

const initTyping = ({
  selector,
  threshold = 0.3,
  speed = 60,
  cursor = '|',
  startDelay = 0,
}) => {
  const element = document.querySelector(selector);
  if (!element) return;

  const text = element.innerHTML.trim();
  if (!text) return;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;

        element.classList.remove('typing-hidden');

        const typewriter = new Typewriter(element, {
          loop: false,
          delay: speed,
          cursor
        });

        typewriter
          .pauseFor(startDelay)
          .callFunction(() => {
            if (element.classList.contains('is-hidden-typing-wait')) {
              element.classList.add('typing-started');
            }
          })
          .typeString(text)
          .start();

        observer.disconnect();
      });
    },
    { threshold }
  );

  observer.observe(element);
};

const initShowcaseStackingCards = ({
  sectionSelector = '.showcase',
  titleSelector = '.showcase__title',
  listSelector = '.showcase__list',
  itemSelector = '.showcase__item',
  boxSelector = '.showcase__box',
  scaleMin = 0.92,
} = {}) => {
  const section = document.querySelector(sectionSelector);
  if (!section) return;

  const title = section.querySelector(titleSelector);
  const list = section.querySelector(listSelector);
  if (!title || !list) return;

  const items = Array.from(list.querySelectorAll(itemSelector));
  const boxes = items.map((item) => item.querySelector(boxSelector));
  if (items.length < 2 || boxes.some((box) => !box)) return;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let rafId = null;

  section.classList.add('is-stacking');

  items.forEach((item, index) => {
    item.style.zIndex = String(index + 1);
  });

  const updateMetrics = () => {
    section.style.setProperty(
      '--showcase-title-height',
      `${title.offsetHeight}px`
    );
  };

  const update = () => {
    rafId = null;

    const viewportHeight = window.innerHeight;
    const sectionScale = section.offsetWidth
      ? section.getBoundingClientRect().width / section.offsetWidth
      : 1;
    const stickyTop =
      Number.parseFloat(window.getComputedStyle(items[0]).top) * sectionScale;
    const lastItemTop = items[items.length - 1].getBoundingClientRect().top;
    const titleOffset = Math.min(0, lastItemTop - stickyTop) / sectionScale;

    title.style.transform = titleOffset < 0
      ? `translate3d(0, ${titleOffset.toFixed(2)}px, 0)`
      : '';

    if (reducedMotion.matches) {
      boxes.forEach((box) => {
        box.style.transform = '';
      });
      return;
    }

    const scrollDistance = Math.max(1, viewportHeight - stickyTop);

    boxes.forEach((box, index) => {
      if (index === boxes.length - 1) {
        box.style.transform = '';
        return;
      }

      const nextTop = items[index + 1].getBoundingClientRect().top;
      const progress = Math.max(
        0,
        Math.min(1, (viewportHeight - nextTop) / scrollDistance)
      );
      const scale = 1 - (1 - scaleMin) * progress;

      box.style.transform = progress > 0
        ? `scale(${scale.toFixed(4)})`
        : '';
    });
  };

  const scheduleUpdate = () => {
    if (rafId !== null) return;
    rafId = window.requestAnimationFrame(update);
  };

  const handleResize = () => {
    updateMetrics();
    scheduleUpdate();
  };

  updateMetrics();
  scheduleUpdate();

  window.addEventListener('scroll', scheduleUpdate, { passive: true });
  window.addEventListener('resize', handleResize, { passive: true });
  window.addEventListener('orientationchange', handleResize);
  reducedMotion.addEventListener('change', scheduleUpdate);

  if ('ResizeObserver' in window) {
    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(title);
  }

  document.fonts?.ready.then(handleResize);
};

document.addEventListener('DOMContentLoaded', () => {
  initTyping({
    selector: '.feature__text',
    threshold: 0.3,
    cursor: '▉',
    startDelay: 2000,
  });

  initSplitTitle();

  initShowcaseStackingCards();
});